import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Phone, Star, CheckCircle2, ShieldCheck, Award, FlaskConical, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import ContactForm from '../components/ContactForm';
import TrustBadges from '../components/TrustBadges';
import ServiceAreaMap from '../components/ServiceAreaMap';
import {
  COMPANY, IMAGES, STATS, SERVICES, PROCESS, WHY_US, TESTIMONIALS, FAQS, PRIMARY_AREAS
} from '../data/mock';

const Home = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  return (
    <div>
      <Seo
        path="/"
        title="Mold Inspection & Air Quality Testing in Ontario"
        description="Independent, IAC2-certified mold inspection and indoor air quality testing across Southern Ontario. Thermal imaging, accredited lab analysis and clear reports in about a week."
        schema={faqSchema}
      />
      {/* HERO */}
      <section className="relative brand-gradient overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#c7e79b]/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-[#d9edc0]/50 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 bg-white/80 border border-[#d3e6bd] text-brand text-sm font-semibold px-4 py-1.5 rounded-full">
              <ShieldCheck className="h-4 w-4" /> IAC2-Certified · Independent Testing
            </span>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.08] font-extrabold text-[#13251b]">
              Certified mold &amp; air quality testing for a <span className="text-brand">healthier home.</span>
            </h1>
            <p className="mt-6 text-lg text-[#44564b] max-w-xl leading-relaxed">
              Professional mold inspection and indoor air quality testing across {COMPANY.region}. We only inspect and test — never remediate — so our findings are always 100% in your best interest.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild className="bg-brand hover:bg-[#5c9a22] text-white rounded-full px-7 h-12 text-base font-semibold group">
                <Link to="/contact">Book an Inspection <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-7 h-12 text-base font-semibold border-[#cbdcbb] text-[#13251b] hover:bg-white">
                <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-1" /> {COMPANY.phone}</a>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#44564b]">
              {['3rd-party lab analysis','No conflict of interest','Report within ~1 week'].map((t) => (
                <span key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand" /> {t}</span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120} className="relative">
            <div className="relative rounded-[1.75rem] overflow-hidden shadow-[0_30px_60px_-20px_rgba(20,50,25,0.35)]">
              <img src={IMAGES.heroHome} alt="Bright, healthy, sunlit home interior" className="w-full h-[420px] lg:h-[500px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 w-56 animate-floaty">
              <div className="flex items-center gap-1 text-[#f5a623]">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="mt-2 text-sm font-semibold text-[#13251b]">Trusted by Ontario homeowners</p>
              <p className="text-xs text-muted-foreground">Clear, honest, certified reporting</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST BADGES */}
      <TrustBadges />

      {/* STATS */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className="text-center">
              <div className="text-3xl lg:text-4xl font-extrabold text-brand font-display">{s.value}</div>
              <div className="mt-1 text-sm text-[#5b6a60]">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-24 bg-[#fafcf7]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center">
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#13251b]">Independent environmental testing you can trust</h2>
            <p className="mt-4 text-[#5b6a60]">Advanced equipment, certified inspectors and accredited labs — combined to give you a clear picture of your indoor environment.</p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={(i % 3) * 90}>
                  <Link to={s.to} className="group block h-full bg-white rounded-2xl p-7 border border-[#eef2e8] hover-lift">
                    <div className="h-14 w-14 rounded-xl bg-[#eaf4dd] flex items-center justify-center group-hover:bg-brand transition-colors">
                      <Icon className="h-7 w-7 text-brand group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-[#13251b]">{s.title}</h3>
                    <p className="mt-3 text-[#5b6a60] text-[15px] leading-relaxed">{s.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-brand font-semibold text-sm group-hover:gap-2 transition-all">
                      Learn more <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <img src={IMAGES.familyHome} alt="Family relaxing comfortably in a healthy home" className="rounded-[1.75rem] w-full h-[440px] object-cover shadow-xl" />
            <div className="absolute -top-5 -right-4 bg-brand text-white rounded-2xl px-6 py-4 shadow-lg">
              <div className="text-2xl font-extrabold font-display">100%</div>
              <div className="text-xs font-medium opacity-90">Independent</div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="text-brand font-semibold text-sm uppercase tracking-wider">Why 360 Mold Services</span>
              <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#13251b]">Honest answers, not a sales pitch</h2>
              <p className="mt-4 text-[#5b6a60]">Because we never perform remediation, you get an unbiased assessment focused entirely on the truth about your property.</p>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {WHY_US.map((w, i) => {
                const Icon = w.icon;
                return (
                  <Reveal key={w.title} delay={i * 80} className="flex gap-4">
                    <div className="h-11 w-11 shrink-0 rounded-lg bg-[#eaf4dd] flex items-center justify-center">
                      <Icon className="h-5 w-5 text-brand" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#13251b]">{w.title}</h4>
                      <p className="mt-1 text-sm text-[#5b6a60] leading-relaxed">{w.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 lg:py-24 dark-section text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center">
            <span className="text-[#8bd44a] font-semibold text-sm uppercase tracking-wider">Our Process</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold">Simple, transparent, thorough</h2>
            <p className="mt-4 text-[#a9bcae]">From your first call to a clear report, here’s exactly what to expect.</p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 90} className="relative bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                  <div className="text-5xl font-extrabold text-white/10 font-display absolute top-3 right-4">{i + 1}</div>
                  <div className="h-12 w-12 rounded-xl bg-[#8bd44a]/15 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-[#8bd44a]" />
                  </div>
                  <h4 className="mt-4 text-lg font-semibold">{p.title}</h4>
                  <p className="mt-2 text-sm text-[#a9bcae] leading-relaxed">{p.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-24 bg-[#fafcf7]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center">
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">Client Stories</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#13251b]">Homeowners &amp; businesses trust us</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 4) * 80} className="bg-white rounded-2xl p-6 border border-[#eef2e8] hover-lift">
                <div className="flex items-center gap-1 text-[#f5a623]">
                  {[...Array(t.rating)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}
                </div>
                <p className="mt-4 text-[15px] text-[#3d4c43] leading-relaxed">“{t.text}”</p>
                <div className="mt-5 pt-4 border-t border-[#eef2e8]">
                  <div className="font-semibold text-[#13251b]">{t.name}</div>
                  <div className="text-sm text-brand flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {t.location}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AREAS + MAP */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <h3 className="text-3xl font-bold text-[#13251b]">Serving communities across {COMPANY.region}</h3>
            <p className="mt-3 text-[#5b6a60]">Certified mold &amp; air quality testing, wherever you are in the region.</p>
          </Reveal>
          <Reveal>
            <ServiceAreaMap />
          </Reveal>
        </div>
      </section>

      {/* FAQ + CONTACT */}
      <section className="py-20 lg:py-24 bg-[#fafcf7]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#13251b]">Common questions</h2>
            <p className="mt-4 text-[#5b6a60]">Still curious? Call us anytime at <a href={COMPANY.phoneHref} className="text-brand font-semibold">{COMPANY.phone}</a>.</p>
            <Accordion type="single" collapsible className="mt-8">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-[#e3ecda]">
                  <AccordionTrigger className="text-left text-[#13251b] font-semibold hover:text-brand">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#5b6a60] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={120}>
            <div className="bg-white rounded-[1.5rem] p-7 lg:p-8 shadow-[0_20px_50px_-24px_rgba(20,50,25,0.25)] border border-[#eef2e8]">
              <div className="flex items-center gap-2 text-brand font-semibold"><FlaskConical className="h-5 w-5" /> Request a Free Quote</div>
              <h3 className="mt-2 text-2xl font-bold text-[#13251b]">Book your inspection</h3>
              <p className="mt-1 text-sm text-[#5b6a60] mb-6">Tell us a little about your property and we’ll get right back to you.</p>
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
